import { createContext, useContext, useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { agentSupabase as supabase } from "../client/agentSupabaseClient";
import delay from "../utils/delay";
import toast from "react-hot-toast";
import NewMessageToast from "../components/NewMessageToast";

const AgentChatContext = createContext(null);

export function AgentChatProvider({ children }) {
    const [view, setView] = useState("lobby");
    const [me, setMe] = useState(null);
    const [users, setUsers] = useState([]);
    const [chatPartner, setChatPartner] = useState(null);
    const [chatRoomId, setChatRoomId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [recentMessagesMap, setRecentMessagesMap] = useState({});
    const [myRoomIds, setMyRoomIds] = useState([]);
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);

    const messagesEndRef = useRef(null);
    const meRef = useRef(null);
    const chatPartnerRef = useRef(null);
    const chatRoomIdRef = useRef(null);
    const isAgentChatOpenRef = useRef(false);

    const { sellerId: authUserId } = useSelector((state) => state.auth);

    useEffect(() => {
        meRef.current = me;
    }, [me]);
    useEffect(() => {
        chatPartnerRef.current = chatPartner;
    }, [chatPartner]);
    useEffect(() => {
        chatRoomIdRef.current = chatRoomId;
    }, [chatRoomId]);
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        (async () => {
            try {
                await delay(2000);
                const { data } = await supabase.auth.getSession();
                if (data.session) {
                    await loadProfile(data.session.user.id);
                }
            } catch (err) {
                console.error("[AgentChat] session load error:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, [authUserId]);

    // ─── Subscribe to ALL user's rooms for toast + recentMessages ───────────
    useEffect(() => {
        if (!me || !myRoomIds.length) return;

        const channels = myRoomIds.map((roomId) =>
            supabase
                .channel(`all-rooms-${roomId}`)
                .on(
                    "postgres_changes",
                    {
                        event: "INSERT",
                        schema: "public",
                        table: "messages",
                        filter: `chat_room_id=eq.${roomId}`
                    },
                    (payload) => {
                        const incoming = payload.new;
                        if (incoming.sender_id === meRef.current?.id) return;

                        supabase
                            .from("users")
                            .select("*")
                            .eq("id", incoming.sender_id)
                            .single()
                            .then(({ data: sender }) => {
                                const enriched = { ...incoming, sender };

                                setRecentMessagesMap((prev) => {
                                    const existing = prev[roomId] ?? [];
                                    return { ...prev, [roomId]: [...existing, enriched].slice(-5) };
                                });

                                const partner = chatPartnerRef.current;
                                const name = sender
                                    ? `${sender.first_name} ${sender.last_name}`.trim()
                                    : partner
                                      ? `${partner.first_name} ${partner.last_name}`.trim()
                                      : "Someone";

                                toast.custom((t) => (
                                    <NewMessageToast
                                        t={t}
                                        id={enriched.id}
                                        name={`New Message from ${name}`}
                                        content={
                                            enriched.media_type === "text"
                                                ? enriched.content.toString()
                                                : "Sent an image."
                                        }
                                    />
                                ));

                                if (
                                    isAgentChatOpenRef.current &&
                                    chatRoomIdRef.current === roomId
                                ) {
                                    supabase
                                        .from("messages")
                                        .update({ is_read: true })
                                        .eq("id", enriched.id)
                                        .then(() => {
                                            setMessages((prev) =>
                                                prev.map((m) =>
                                                    m.id === enriched.id
                                                        ? { ...m, is_read: true }
                                                        : m
                                                )
                                            );
                                        });
                                }
                            });
                    }
                )
                .subscribe()
        );

        return () => channels.forEach((ch) => supabase.removeChannel(ch));
    }, [me, myRoomIds]);

    // ─── Active chat room realtime (messages state only) ─────────────────────
    useEffect(() => {
        if (!chatRoomId) return;
        const channel = supabase
            .channel(`room-${chatRoomId}`)
            .on(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "messages",
                    filter: `chat_room_id=eq.${chatRoomId}`
                },
                (payload) => {
                    const incoming = payload.new;
                    supabase
                        .from("users")
                        .select("*")
                        .eq("id", incoming.sender_id)
                        .single()
                        .then(({ data: sender }) => {
                            const enriched = { ...incoming, sender };
                            setMessages((prev) => [...prev, enriched]);
                        });
                }
            )
            .on(
                "postgres_changes",
                {
                    event: "UPDATE",
                    schema: "public",
                    table: "messages",
                    filter: `chat_room_id=eq.${chatRoomId}`
                },
                (payload) => {
                    setMessages((prev) =>
                        prev.map((m) => (m.id === payload.new.id ? { ...m, ...payload.new } : m))
                    );
                }
            )
            .subscribe();
        return () => supabase.removeChannel(channel);
    }, [chatRoomId]);

    useEffect(() => {
        if (!me) return;
        const channel = supabase
            .channel("users-online")
            .on(
                "postgres_changes",
                { event: "UPDATE", schema: "public", table: "users" },
                (payload) => {
                    setUsers((prev) =>
                        prev.map((u) => (u.id === payload.new.id ? { ...u, ...payload.new } : u))
                    );
                    setChatPartner((prev) =>
                        prev?.id === payload.new.id ? { ...prev, ...payload.new } : prev
                    );
                }
            )
            .subscribe();
        return () => {
            supabase.removeChannel(channel);
        };
    }, [me]);

    useEffect(() => {
        if (!me) return;
        const sendHeartbeat = async () => {
            await supabase
                .from("users")
                .update({ is_online: true, last_seen: new Date().toISOString() })
                .eq("id", me.id);
        };
        sendHeartbeat();
        const interval = setInterval(sendHeartbeat, 20_000);
        return () => clearInterval(interval);
    }, [me]);

    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === "visible") {
                isAgentChatOpenRef.current = true;
                if (chatRoomId) {
                    const { data: msgs } = await supabase
                        .from("messages")
                        .select("*, sender:users!messages_sender_id_fkey(*)")
                        .eq("chat_room_id", chatRoomId)
                        // .neq("sender_id", meRef.current?.id)
                        .order("created_at", { ascending: true });
                    if (msgs) {
                        setMessages(msgs);
                        const unreadIds = msgs
                            .filter((m) => !m.is_read && m.sender_id !== meRef.current?.id)
                            .map((m) => m.id);
                        if (unreadIds.length > 0) {
                            await supabase
                                .from("messages")
                                .update({ is_read: true })
                                .in("id", unreadIds);
                            setMessages((prev) =>
                                prev.map((m) =>
                                    unreadIds.includes(m.id) ? { ...m, is_read: true } : m
                                )
                            );
                        }
                    }
                }
                await supabase.auth.refreshSession();
            } else {
                isAgentChatOpenRef.current = false;
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [chatRoomId]);

    useEffect(() => {
        if (!me || me.role !== "AGENT") return;
        const channel = supabase
            .channel("new-chat-rooms")
            .on(
                "postgres_changes",
                { event: "INSERT", schema: "public", table: "chat_rooms" },
                async (payload) => {
                    const room = payload.new;
                    if (room.user1_id !== me.id && room.user2_id !== me.id) return;
                    const newUserId = room.user1_id === me.id ? room.user2_id : room.user1_id;
                    const { data: newUser } = await supabase
                        .from("users")
                        .select("*")
                        .eq("id", newUserId)
                        .single();
                    if (newUser) {
                        setUsers((prev) =>
                            prev.find((u) => u.id === newUser.id) ? prev : [...prev, newUser]
                        );
                    }
                    setMyRoomIds((prev) => (prev.includes(room.id) ? prev : [...prev, room.id]));
                }
            )
            .subscribe();
        return () => supabase.removeChannel(channel);
    }, [me]);

    const loadProfile = async (userId) => {
        const { data, error } = await supabase
            .from("users")
            .select("*")
            .eq("id, role", userId)
            .single();
        if (error || !data) return;
        setMe(data);
        await supabase
            .from("users")
            .update({ is_online: true, last_seen: new Date().toISOString() })
            .eq("id", userId);
        await loadUsers(userId, data.role);
    };

    const loadUsers = async (myId, myRole) => {
        if (myRole === "USER") {
            const { data } = await supabase.from("users").select("*").eq("role", "AGENT");
            setUsers(data || []);
        } else if (myRole === "AGENT") {
            const { data } = await supabase
                .from("chat_rooms")
                .select(
                    "*, u1:users!chat_rooms_user1_id_fkey(*), u2:users!chat_rooms_user2_id_fkey(*)"
                )
                .or(`user1_id.eq.${myId},user2_id.eq.${myId}`);

            const seen = new Set();
            const uniqueUsers = (data || [])
                .map((room) => (room.user1_id === myId ? room.u2 : room.u1))
                .filter((u) => {
                    if (!u || seen.has(u.id)) return false;
                    seen.add(u.id);
                    return true;
                });
            setUsers(uniqueUsers);
        }
        await preloadRecentMessages(myId);
    };

    const preloadRecentMessages = async (myId) => {
        const { data: rooms } = await supabase
            .from("chat_rooms")
            .select("id")
            .or(`user1_id.eq.${myId},user2_id.eq.${myId}`);

        if (!rooms?.length) return;

        setMyRoomIds(rooms.map((r) => r.id));

        const results = await Promise.all(
            rooms.map((room) =>
                supabase
                    .from("messages")
                    .select("*, sender:users!messages_sender_id_fkey(*)")
                    .eq("chat_room_id", room.id)
                    // .neq("sender_id", myId)
                    .order("created_at", { ascending: false })
                    .limit(5)
                    .then(({ data }) => ({ roomId: room.id, msgs: (data || []).reverse() }))
            )
        );

        setRecentMessagesMap(Object.fromEntries(results.map(({ roomId, msgs }) => [roomId, msgs])));
    };

    const signInToSupabase = async ({
        email,
        password,
        firstName,
        lastName,
        neonmallId,
        avatarUrl
    }) => {
        setLoading(true);
        try {
            const derived = email.split("@")[0];
            const buildUserRecord = (userId) => ({
                id: userId,
                neonmall_id: neonmallId ?? derived,
                first_name: firstName ?? derived,
                last_name: lastName ?? "",
                avatar_url: avatarUrl ?? "",
                role: "AGENT"
            });

            const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword(
                { email, password }
            );

            if (!signInError && signInData.user) {
                const { data: existing } = await supabase
                    .from("users")
                    .select("neonmall_id, first_name, last_name, avatar_url, role")
                    .eq("id", signInData.user.id)
                    .single();

                if (!existing) {
                    await supabase.from("users").insert(buildUserRecord(signInData.user.id));
                } else {
                    const record = buildUserRecord(signInData.user.id);
                    const hasChanges =
                        existing.neonmall_id !== record.neonmall_id ||
                        existing.first_name !== record.first_name ||
                        existing.last_name !== record.last_name ||
                        existing.avatar_url !== record.avatar_url;
                    if (hasChanges) {
                        const { neonmall_id, first_name, last_name, avatar_url } = record;
                        await supabase
                            .from("users")
                            .update({ neonmall_id, first_name, last_name, avatar_url })
                            .eq("id", signInData.user.id);
                    }
                }
                await loadProfile(signInData.user.id);
                return;
            }

            const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                email,
                password
            });
            if (signUpError || !signUpData.user) return;
            await supabase.from("users").insert(buildUserRecord(signUpData.user.id));
            await loadProfile(signUpData.user.id);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        if (me) {
            await supabase
                .from("users")
                .update({ is_online: false, last_seen: new Date().toISOString() })
                .eq("id", me.id);
        }
        isAgentChatOpenRef.current = false;
        await supabase.auth.signOut();
        setMe(null);
        setUsers([]);
        setChatPartner(null);
        setChatRoomId(null);
        setMessages([]);
        setRecentMessagesMap({});
        setMyRoomIds([]);
    };

    const openChat = async (partner) => {
        setChatPartner(partner);

        let { data: room } = await supabase
            .from("chat_rooms")
            .select("*")
            .or(
                `and(user1_id.eq.${me.id},user2_id.eq.${partner.id}),and(user1_id.eq.${partner.id},user2_id.eq.${me.id})`
            )
            .single();

        if (!room) {
            const { data: newRoom } = await supabase
                .from("chat_rooms")
                .insert({ user1_id: me.id, user2_id: partner.id })
                .select()
                .single();
            room = newRoom;
            setMyRoomIds((prev) => (prev.includes(room.id) ? prev : [...prev, room.id]));
        }

        const { data: msgs } = await supabase
            .from("messages")
            .select("*, sender:users!messages_sender_id_fkey(*)")
            .eq("chat_room_id", room.id)
            // .neq("sender_id", me.id)
            .order("created_at", { ascending: true });

        const loadedMsgs = msgs || [];
        const unreadIds = loadedMsgs
            .filter((m) => !m.is_read && m.sender_id !== me.id)
            .map((m) => m.id);
        if (unreadIds.length > 0) {
            await supabase.from("messages").update({ is_read: true }).in("id", unreadIds);
        }
        const readyMsgs = loadedMsgs.map((m) =>
            unreadIds.includes(m.id) ? { ...m, is_read: true } : m
        );

        setMessages(readyMsgs);
        setRecentMessagesMap((prev) => ({ ...prev, [room.id]: readyMsgs.slice(-5) }));
        setChatRoomId(room.id);
        setView("chat");
        isAgentChatOpenRef.current = true;
    };

    const sendMessage = async (newMessage, mediaType = "text") => {
        if (!newMessage.trim() || !chatRoomId || sending) return;
        setSending(true);
        const payload = {
            chat_room_id: chatRoomId,
            sender_id: me.id,
            media_type: mediaType,
            content: newMessage.trim()
        };
        const { error } = await supabase.from("messages").insert(payload);
        if (error) {
            await supabase.auth.refreshSession();
            await supabase.from("messages").insert(payload);
        }
        setSending(false);
    };

    const formatTime = (iso) =>
        new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    const formatLastSeen = (iso) => {
        const d = new Date(iso);
        return `Last seen ${d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    };

    const recentMessages = useMemo(() => {
        return Object.values(recentMessagesMap)
            .flatMap((item) => (Array.isArray(item) ? item : [item]))
            .filter((msg) => msg.sender_id !== me?.id)
            .slice(0, 5);
    }, [recentMessagesMap, me?.id]);

    return (
        <AgentChatContext.Provider
            value={{
                view,
                me,
                users,
                chatPartner,
                messages,
                recentMessages,
                recentMessagesMap,
                sending,
                loading,
                messagesEndRef,
                signInToSupabase,
                handleLogout,
                openChat,
                sendMessage,
                formatTime,
                formatLastSeen
            }}
        >
            {children}
        </AgentChatContext.Provider>
    );
}

export function useAgentChat() {
    const ctx = useContext(AgentChatContext);
    if (!ctx) throw new Error("useAgentChat must be used within an AgentChatProvider");
    return ctx;
}
